import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Box, Container, Grid, Typography } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ContentWrapper } from "../components/layout/ContentWrapper";
import { AnimatedView } from "../components/utils/AnimatedView";
import { BottomActionBar } from "../components/utils/BottomActionBar";
import { ExternalLink } from "../components/utils/ExternalLink";
import ReadingBar from "../components/utils/ReadingBar";
import RenderContent from "../components/utils/RenderContent";
import { BreadCrumbs } from "../components/view/BreadCrumbs";
import { ProjectCard } from "../components/view/ProjectCard";
import { routes } from "../constants/routes";
import { ContentfulContext } from "../context/ContentfulContext";
import { MarketingClaim, Project, References, Solution, Solutions } from "../models/schema";
import { colors } from "../theme/colors";

const ProjectPage = () => {
  const { content } = useContext(ContentfulContext);
  const solutions = content?.solutions as Solutions;
  const references = content?.references as References;
  const { publicSectorCollection, privateSectorCollection, partnershipCollection } = references;
  const { solutionsCollection, bottomBannerActionProject, bottomBannerLabelProject } = solutions;

  const partners = useMemo(
    () => [
      ...(publicSectorCollection?.items || []),
      ...(privateSectorCollection?.items || []),
      ...(partnershipCollection?.items || [])
    ],
    [publicSectorCollection, privateSectorCollection, partnershipCollection]
  );

  const [solution, setSolution] = useState<Solution>();
  const [project, setProject] = useState<Project>();
  const [isSubproject, setIsSubProject] = useState<boolean>(false);

  const { id } = useParams();

  useEffect(() => {
    solutionsCollection?.items.forEach((solution) => {
      if (solution?.primaryProject?.id === id) {
        setSolution(solution as Solution);
        setProject(solution?.primaryProject as Project);
        setIsSubProject(false);
      }

      const subProject = solution?.secondaryProjectsCollection?.items.find(
        (project) => project?.id === id
      );

      if (subProject) {
        setSolution(solution as Solution);
        setProject(subProject);
        setIsSubProject(true);
      }
    });
  }, [id, solutions, solutionsCollection?.items]);

  const breadcrumbs = useMemo(() => {
    if (!solution) return [];

    if (isSubproject) {
      return [
        { label: solutions.title as string, route: routes.solutions },
        {
          label: solution?.primaryProject?.title as string,
          route: `${routes.project}/${solution?.primaryProject?.id}`
        },
        {
          label: project?.title as string,
          route: `${routes.project}/${id}`
        }
      ];
    }

    return [
      { label: solutions.title as string, route: routes.solutions },
      {
        label: project?.title as string,
        route: `${routes.project}/${id}`
      }
    ];
  }, [id, isSubproject, project?.title, solution, solutions.title]);

  const partnerLogo = (id?: string) => {
    const partner = partners.find((partner) => partner?.sys.id === id);

    if (!partner) return null;

    return (
      <ExternalLink href={partner.url as string} target="_blank" rel="noopener noreferrer">
        <Box
          component="img"
          src={partner?.logo?.url as string}
          sx={{ width: { xs: "12rem", md: "15rem" } }}
        />
      </ExternalLink>
    );
  };

  const marketingClaim: MarketingClaim | undefined = useMemo(
    () => project?.marketingClaimsCollection?.items?.[0] as MarketingClaim,
    [project]
  );

  return (
    <>
      <ReadingBar />

      <ContentWrapper
        title={project?.title as string}
        // subTitle={project?.readingTime as string} // Project reading time currently disabled
        sx={{ pb: 10, overflowX: "hidden" }}>
        <BreadCrumbs breadcrumbs={breadcrumbs} />
        <Container sx={{ display: "flex", flexDirection: "column", gap: { xs: 5, md: 10 } }}>
          <RenderContent
            content={project?.description?.json}
            sx={{
              "& > :not(template) ~ :not(template)": {
                mb: 5
              }
            }}
            childrenPosition="before"
            options={{
              renderNode: {
                [BLOCKS.HR]: () => (
                  <Box sx={{ position: "relative", my: 5, height: { xs: "10rem", md: "15rem" } }}>
                    <Box
                      sx={{
                        position: "absolute",
                        left: "-500%",
                        width: "1100%",
                        height: "100%",
                        backgroundColor: colors.grey5,
                        p: { xs: 2, md: 4 },
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center"
                      }}>
                      <Container
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                          maxWidth: window.innerWidth
                        }}>
                        <AnimatedView animation="popIn">
                          <Typography
                            sx={{ fontSize: { xs: "2rem", md: "4rem" }, fontWeight: "100" }}
                            color={colors.yellow}>
                            {marketingClaim?.number}
                          </Typography>
                          <RenderContent
                            content={marketingClaim?.text?.json}
                            options={{
                              renderNode: {
                                [BLOCKS.PARAGRAPH]: (node, children) => (
                                  <Typography
                                    color={colors.white}
                                    sx={{ fontSize: { xs: "1rem", md: "1.3rem" }, mb: 3 }}>
                                    {children}
                                  </Typography>
                                )
                              }
                            }}
                          />
                        </AnimatedView>
                      </Container>
                    </Box>
                  </Box>
                )
              }
            }}>
            <AnimatedView animation="fadeIn">
              <RenderContent
                content={project?.attributes?.json}
                sx={{ float: "right", mb: 5, ml: { md: 5 } }}
                options={{
                  renderNode: {
                    [BLOCKS.TABLE]: (node, children) => (
                      <Box
                        component="table"
                        sx={{
                          p: 3,
                          border: `2px solid ${colors.grey2}`,
                          borderSpacing: 0,
                          maxWidth: "400px"
                        }}>
                        <tbody>{children}</tbody>
                      </Box>
                    ),
                    [BLOCKS.TABLE_CELL]: (node, children) => (
                      <Box
                        component="td"
                        sx={{
                          "&:first-of-type": {
                            borderRight: `2px solid ${colors.grey2}`,
                            pr: { xs: 3, md: 5 },
                            mr: 2
                          },
                          "&:nth-of-type(2)": {
                            pl: { xs: 3, md: 5 }
                          },
                          py: { xs: 2, md: 4 }
                        }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start"
                          }}>
                          {children}
                        </Box>
                      </Box>
                    ),
                    [INLINES.EMBEDDED_ENTRY]: (node) => partnerLogo(node?.data?.target?.sys?.id)
                  }
                }}
              />
            </AnimatedView>
          </RenderContent>
          {!isSubproject && (solution?.secondaryProjectsCollection?.items?.length || 0) > 0 && (
            <>
              <Typography variant="h2" textAlign="center">
                {solutions?.secondaryProjectsTitle}
              </Typography>

              <Grid container spacing={5}>
                {solution?.secondaryProjectsCollection?.items.map((project, index) => (
                  <Grid item xs={12} md={4} key={project?.id}>
                    <AnimatedView
                      animation="fadeIn"
                      delay={{
                        xs: 0,
                        md: index * 300
                      }}
                      sx={{ height: "100%" }}>
                      <ProjectCard project={project as Project} />
                    </AnimatedView>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Container>
      </ContentWrapper>
      <BottomActionBar
        actionLabel={bottomBannerActionProject as string}
        title={bottomBannerLabelProject as string}
        route={routes.contact}
      />
    </>
  );
};

export default ProjectPage;
